const BucketStorage = {
	getKey(productId) {
		return `build-your-bucket-${productId}`;
	},

	load(key) {
		try {
			const stored = sessionStorage.getItem(key);
			return stored ? JSON.parse(stored) : null;
		} catch {
			return null;
		}
	},

	save(key, state) {
		sessionStorage.setItem(key, JSON.stringify(state));
	},

	clear(key) {
		try {
			sessionStorage.removeItem(key);
		} catch {
			return;
		}
	},

	createInitialState(config) {
		const step3Seasonings = (config.step3Seasonings || []).map(seasoning => ({
			...seasoning,
			selected: false,
			selectionOrder: null,
			effectivePrice: seasoning.price,
			isFree: false,
			isNonProduct: Boolean(seasoning.isNonProduct)
		}));

		return {
			mainProduct: config.mainProduct,
			bucketBase: config.bucketBase,
			step1Complete: false,
			step4Complete: false,
			furthestStepIndex: 0,
			step2Products: (config.step2Products || []).map(product => ({
				...product,
				quantity: product.quantity || 0
			})),
			step3Seasonings: this.applySeasoningPricing(
				this.applyDefaultSeasoningSelection(step3Seasonings)
			),
			step4Gift: {
				enabled: false,
				message: ''
			},
			included: config.included || ''
		};
	},

	mergeWithConfig(storedState, config) {
		const initialState = this.createInitialState(config);

		if (!storedState) return initialState;

		return {
			...initialState,
			...storedState,
			mainProduct: initialState.mainProduct,
			bucketBase: initialState.bucketBase,
			included: initialState.included,
			step2Products: initialState.step2Products.map(product => {
				const storedProduct = (storedState.step2Products || []).find(
					item => item.id === product.id
				);

				return {
					...product,
					quantity: storedProduct?.quantity || 0
				};
			}),
			step3Seasonings: BucketStorage.applySeasoningPricing(
				BucketStorage.applyDefaultSeasoningSelection(
					initialState.step3Seasonings,
					storedState.step3Seasonings
				)
			),
			step4Gift: {
				enabled: storedState.step4Gift?.enabled || false,
				message: storedState.step4Gift?.message || ''
			},
			step1Complete: storedState.step1Complete || false,
			step4Complete: storedState.step4Complete || false,
			furthestStepIndex: Math.max(
				storedState.furthestStepIndex ?? 0,
				this.getInferredFurthestStepIndex(storedState, config)
			)
		};
	},

	isStepComplete(stepIndex, state, config) {
		switch (stepIndex) {
			case 0:
				return this.isStep1Complete(state);
			case 1:
				return this.isStep2Complete(state, config);
			case 2:
				return this.isStep3Complete(state);
			case 3:
				return this.isStep4Complete(state);
			case 4:
				return this.isStep4Complete(state);
			default:
				return false;
		}
	},

	areStepsValidThrough(stepIndex, state, config) {
		for (let index = 0; index <= stepIndex; index += 1) {
			if (!this.isStepComplete(index, state, config)) return false;
		}

		return true;
	},

	isStepperItemComplete(stepIndex, state, config, activeIndex) {
		if (stepIndex === activeIndex) return false;

		if (!this.areStepsValidThrough(stepIndex, state, config)) return false;

		const resumeIndex = this.getResumeStepIndex(state, config);
		const furthestIndex = state.furthestStepIndex ?? 0;

		return stepIndex < resumeIndex || stepIndex <= furthestIndex;
	},

	getReachableStepIndex(state, config) {
		return this.getResumeStepIndex(state, config);
	},

	getInferredFurthestStepIndex(state, config) {
		if (this.isStep4Complete(state)) return 4;

		if (this.isStep3Complete(state)) return 3;

		if (this.isStep2Complete(state, config)) return 2;

		if (this.isStep1Complete(state)) return 1;

		return 0;
	},

	getStep2Total(state) {
		return (state.step2Products || []).reduce(
			(total, product) => total + (product.quantity || 0),
			0
		);
	},

	hasLaterProgress(state) {
		const furthestIndex = state.furthestStepIndex ?? 0;

		return (
			this.getStep2Total(state) > 0 ||
			state.step4Complete ||
			(furthestIndex >= 2 &&
				(state.step3Seasonings || []).some(seasoning => seasoning.selected))
		);
	},

	isStep1Complete(state) {
		return (
			Boolean(state.mainProduct?.id) &&
			(state.step1Complete || this.hasLaterProgress(state))
		);
	},

	isStep2Complete(state, config) {
		const minItems = Number(config.minItems) || 3;

		return this.getStep2Total(state) >= minItems;
	},

	isStep3Complete(state) {
		const furthestIndex = state.furthestStepIndex ?? 0;

		if (furthestIndex < 3) return false;

		return (state.step3Seasonings || []).some(seasoning => seasoning.selected);
	},

	isStep4Complete(state) {
		return Boolean(state.step4Complete);
	},

	getResumeStepIndex(state, config) {
		if (!this.isStep1Complete(state)) return 0;

		if (!this.isStep2Complete(state, config)) return 1;

		if (!this.isStep3Complete(state)) return 2;

		if (!this.isStep4Complete(state)) return 3;

		return 4;
	},

	applyDefaultSeasoningSelection(seasonings, storedSeasonings) {
		const merged = seasonings.map(seasoning => {
			if (!storedSeasonings) {
				return {
					...seasoning,
					selected: false,
					selectionOrder: null
				};
			}

			const storedSeasoning = storedSeasonings.find(item => item.id === seasoning.id);

			return {
				...seasoning,
				selected: storedSeasoning?.selected || false,
				selectionOrder: storedSeasoning?.selectionOrder ?? null
			};
		});

		if (merged.some(seasoning => seasoning.selected)) return merged;

		const defaultSeasoning = this.getDefaultSeasoning(merged);

		if (!defaultSeasoning) return merged;

		return merged.map(seasoning => {
			if (seasoning.id === defaultSeasoning.id) {
				return {
					...seasoning,
					selected: true,
					selectionOrder: 1
				};
			}

			return {
				...seasoning,
				selected: false,
				selectionOrder: null
			};
		});
	},

	applySeasoningPricing(seasonings) {
		const selectedSeasonings = seasonings
			.filter(seasoning => seasoning.selected && !seasoning.isNonProduct)
			.sort(
				(a, b) => (a.selectionOrder ?? Infinity) - (b.selectionOrder ?? Infinity)
			);

		return seasonings.map(seasoning => {
			if (!seasoning.selected || seasoning.isNonProduct) {
				return {
					...seasoning,
					effectivePrice: seasoning.isNonProduct ? 0 : seasoning.price,
					isFree: false
				};
			}

			const isFirstSelected = selectedSeasonings[0]?.id === seasoning.id;

			return {
				...seasoning,
				effectivePrice: isFirstSelected ? 0 : seasoning.price,
				isFree: isFirstSelected
			};
		});
	},

	isClassicSeasoning(seasoning) {
		const handle = String(seasoning?.handle || '').toLowerCase();
		const title = String(seasoning?.title || '').trim().toLowerCase();

		return handle === 'classic' || title === 'classic seasoning' || title === 'classic';
	},

	getDefaultSeasoning(seasonings) {
		const productSeasonings = (seasonings || []).filter(
			seasoning => !seasoning.isNonProduct
		);

		return (
			productSeasonings.find(seasoning => this.isClassicSeasoning(seasoning)) ||
			productSeasonings[0]
		);
	},

	isProductSeasoning(seasoning) {
		return Boolean(
			seasoning?.selected && !seasoning?.isNonProduct && seasoning?.variantId
		);
	},

	formatMoney(cents, moneyFormat) {
		const amount = (Number(cents) / 100).toFixed(2);
		const amountNoDecimals = String(Math.round(Number(cents) / 100));

		if (moneyFormat) {
			return moneyFormat
				.replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount)
				.replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals)
				.replace(/\{\{\s*amount\s*\}\}/g, amount);
		}

		return `$${amount}`;
	},

	replacePlaceholders(template, values = {}) {
		return Object.entries(values).reduce(
			(result, [key, value]) =>
				result.replace(new RegExp(`\\[${key}\\]`, 'g'), value ?? ''),
			template
		);
	}
};

class BuildYourBucket extends HTMLElement {
	selectors = {
		step: '[data-step]',
		stepButton: '[data-step-button]',
		stepper: '.bucket-stepper',
		stepperItem: '[data-stepper-step]',
		stepperNumber: '.bucket-stepper__number',
		stepperCheck: '.bucket-stepper__check',
		config: '[data-bucket-config]',
		exitLink: '[data-bucket-exit]'
	};

	stateClasses = {
		hidden: 'is-hidden',
		active: 'is-active',
		complete: 'is-complete'
	};

	currentStepIndex = 0;

	connectedCallback() {
		this.container = this.closest('.tr-container');
		this.config = this.getConfig();
		this.storageKey = BucketStorage.getKey(this.config.mainProduct.id);
		this.state = BucketStorage.mergeWithConfig(
			BucketStorage.load(this.storageKey),
			this.config
		);

		this.steps = [...this.querySelectorAll(this.selectors.step)].sort(
			(a, b) => Number(a.dataset.step) - Number(b.dataset.step)
		);
		this.stepperItems = this.container
			? [...this.container.querySelectorAll(this.selectors.stepperItem)]
			: [];

		this.currentStepIndex = BucketStorage.getResumeStepIndex(
			this.state,
			this.config
		);

		this.addEventListener('click', this.onClick);
		this.container?.addEventListener('click', this.onStepperClick);
		this.container?.addEventListener('click', this.onExitClick);
		this.addEventListener('bucket:next-step', this.onNextStep);
		this.addEventListener('bucket:prev-step', this.onPrevStep);
		this.addEventListener('bucket:go-to-step', this.onGoToStep);
		this.addEventListener('bucket:update-state', this.onUpdateState);

		document.addEventListener('shopify:section:load', this.onSectionLoad);

		if (Shopify.designMode) {
			document.addEventListener('shopify:block:select', this.onBlockSelect);
		}

		this.hydrateSteps();
		this.showStep(this.currentStepIndex);
	}

	disconnectedCallback() {
		this.removeEventListener('click', this.onClick);
		this.container?.removeEventListener('click', this.onStepperClick);
		this.container?.removeEventListener('click', this.onExitClick);
		this.removeEventListener('bucket:next-step', this.onNextStep);
		this.removeEventListener('bucket:prev-step', this.onPrevStep);
		this.removeEventListener('bucket:go-to-step', this.onGoToStep);
		this.removeEventListener('bucket:update-state', this.onUpdateState);

		document.removeEventListener('shopify:section:load', this.onSectionLoad);

		if (Shopify.designMode) {
			document.removeEventListener('shopify:block:select', this.onBlockSelect);
		}
	}

	getConfig() {
		const configElement = this.querySelector(this.selectors.config);

		if (!configElement) return { mainProduct: { id: 0 }, step2Products: [], step3Seasonings: [] };

		try {
			return JSON.parse(configElement.textContent);
		} catch {
			return { mainProduct: { id: 0 }, step2Products: [], step3Seasonings: [] };
		}
	}

	onSectionLoad = event => {
		if (
			!this.closest(`#shopify-section-${event.detail.sectionId}`) &&
			!this.closest(`#${event.detail.sectionId}`)
		)
			return;

		this.config = this.getConfig();
		this.state = BucketStorage.mergeWithConfig(
			BucketStorage.load(this.storageKey),
			this.config
		);

		this.steps = [...this.querySelectorAll(this.selectors.step)].sort(
			(a, b) => Number(a.dataset.step) - Number(b.dataset.step)
		);
		this.stepperItems = this.container
			? [...this.container.querySelectorAll(this.selectors.stepperItem)]
			: [];

		this.currentStepIndex = BucketStorage.getResumeStepIndex(
			this.state,
			this.config
		);

		this.hydrateSteps();
		this.showStep(this.currentStepIndex);
	};

	onBlockSelect = event => {
		if (!this.contains(event.target)) return;

		const stepElement = event.target.closest('[data-step]');

		if (stepElement) {
			this.goToStep(Number(stepElement.dataset.step));
			return;
		}

		const stepperItem = event.target.closest('[data-stepper-step]');

		if (!stepperItem) return;

		const stepperIndex = this.stepperItems.indexOf(stepperItem);

		if (stepperIndex >= 0) this.showStep(stepperIndex);
	};

	onClick = event => {
		const stepButton = event.target.closest(this.selectors.stepButton);

		if (!stepButton || !this.contains(stepButton)) return;

		this.state.step1Complete = true;
		this.saveState();
		this.goNext();
	};

	onExitClick = event => {
		const exitLink = event.target.closest(this.selectors.exitLink);

		if (!exitLink || !this.container?.contains(exitLink)) return;

		this.resetState();
	};

	onStepperClick = event => {
		const stepperItem = event.target.closest(this.selectors.stepperItem);

		if (!stepperItem || !this.container?.contains(stepperItem)) return;

		const stepperIndex = this.stepperItems.indexOf(stepperItem);

		if (stepperIndex < 0 || stepperIndex === this.currentStepIndex) return;

		const reachableIndex = BucketStorage.getReachableStepIndex(
			this.state,
			this.config
		);

		if (stepperIndex > reachableIndex) return;

		this.showStep(stepperIndex);
	};

	onNextStep = () => {
		this.goNext();
	};

	onPrevStep = () => {
		this.goPrev();
	};

	onGoToStep = event => {
		this.goToStep(Number(event.detail.step));
	};

	onUpdateState = event => {
		this.state = {
			...this.state,
			...event.detail
		};

		if (event.detail.step3Seasonings) {
			this.state.step3Seasonings = BucketStorage.applySeasoningPricing(
				event.detail.step3Seasonings
			);
		}

		this.saveState();
		this.updateStepper(this.currentStepIndex);
		this.dispatchStateUpdated();
	};

	saveState() {
		BucketStorage.save(this.storageKey, this.state);
	}

	resetState() {
		BucketStorage.clear(this.storageKey);
		this.state = BucketStorage.createInitialState(this.config);
		this.currentStepIndex = 0;
		this.hydrateSteps();
		this.showStep(0);
	}

	dispatchStateUpdated() {
		this.dispatchEvent(
			new CustomEvent('bucket:state-updated', {
				bubbles: false,
				detail: this.state
			})
		);
	}

	getState() {
		return this.state;
	}

	getCurrentStepIndex() {
		return this.currentStepIndex;
	}

	hydrateSteps() {
		this.dispatchStateUpdated();
	}

	goNext() {
		if (this.currentStepIndex >= this.steps.length - 1) return;

		const currentStep = this.steps[this.currentStepIndex];

		if (currentStep?.tagName === 'STEP-FOUR-COMPONENT') {
			this.state.step4Complete = true;
			this.saveState();
		}

		this.currentStepIndex += 1;
		this.showStep(this.currentStepIndex);
		this.scrollToActiveStep();
	}

	goPrev() {
		if (this.currentStepIndex <= 0) return;

		this.currentStepIndex -= 1;
		this.showStep(this.currentStepIndex);
		this.scrollToActiveStep();
	}

	goToStep(stepNumber) {
		const stepIndex = this.steps.findIndex(
			step => Number(step.dataset.step) === stepNumber
		);

		if (stepIndex === -1) return;

		this.showStep(stepIndex);
	}

	showStep(index) {
		this.currentStepIndex = index;

		if (index > (this.state.furthestStepIndex ?? 0)) {
			this.state.furthestStepIndex = index;
			this.saveState();
		}

		this.steps.forEach((step, stepIndex) => {
			step.classList.toggle(
				this.stateClasses.hidden,
				stepIndex !== index
			);
		});

		this.updateStepper(index);
		this.dispatchStateUpdated();
	}

	scrollToActiveStep() {
		const target =
			this.container?.querySelector(this.selectors.stepper) ||
			this.steps[this.currentStepIndex] ||
			this;

		if (!target) return;

		const prefersReducedMotion = window.matchMedia(
			'(prefers-reduced-motion: reduce)'
		).matches;

		target.scrollIntoView({
			block: 'start',
			behavior: prefersReducedMotion ? 'auto' : 'smooth'
		});
	}

	updateStepper(activeIndex) {
		const reachableIndex = BucketStorage.getReachableStepIndex(
			this.state,
			this.config
		);

		this.stepperItems.forEach((item, itemIndex) => {
			const number = item.querySelector(this.selectors.stepperNumber);
			const check = item.querySelector(this.selectors.stepperCheck);
			const isComplete = BucketStorage.isStepperItemComplete(
				itemIndex,
				this.state,
				this.config,
				activeIndex
			);
			const isActive = itemIndex === activeIndex;
			const isClickable =
				itemIndex !== activeIndex && itemIndex <= reachableIndex;
			const isReachable =
				isClickable && !isComplete && itemIndex > activeIndex;

			item.classList.toggle(this.stateClasses.complete, isComplete);
			item.classList.toggle(this.stateClasses.active, isActive);
			item.classList.toggle('is-clickable', isClickable);
			item.classList.toggle('is-reachable', isReachable);

			if (number) number.hidden = isComplete;
			if (check) check.hidden = !isComplete;
		});
	}
}

class StepTwoComponent extends HTMLElement {
	selectors = {
		productCard: '[data-bucket-product-card]',
		qtyIncrease: '[data-qty-increase]',
		qtyDecrease: '[data-qty-decrease]',
		qtyValue: '[data-qty-value]',
		limitMessage: '[data-limit-message]',
		statusCount: '[data-bucket-status-count]',
		statusMessage: '[data-bucket-status-message]',
		stepContinue: '[data-step-continue]',
		stepBack: '[data-step-back]',
		fullPopup: '[data-bucket-full-popup]',
		fullClose: '[data-bucket-full-close]'
	};

	stateClasses = {
		selected: 'is-selected',
		disabled: 'is-disabled'
	};

	connectedCallback() {
		this.root = this.closest('build-your-bucket');
		this.minItems = Number(this.dataset.minItems) || 3;
		this.maxItems = Number(this.dataset.maxItems) || 4;
		this.statusCountText =
			this.dataset.statusCountText || '[count] of [min] minimum';
		this.statusMessageText =
			this.dataset.statusMessageText ||
			'ADD AT LEAST [min] ITEMS TO CONTINUE';

		this.productCards = [...this.querySelectorAll(this.selectors.productCard)];
		this.statusCountElement = this.querySelector(this.selectors.statusCount);
		this.statusMessageElement = this.querySelector(
			this.selectors.statusMessage
		);
		this.continueButton = this.querySelector(this.selectors.stepContinue);
		this.fullPopup = this.querySelector(this.selectors.fullPopup);

		this.addEventListener('click', this.onClick);
		this.root?.addEventListener('bucket:state-updated', this.onStateUpdated);

		document.addEventListener('shopify:section:load', this.onSectionLoad);

		this.hydrateFromState();
		this.updateUI();
	}

	disconnectedCallback() {
		this.removeEventListener('click', this.onClick);
		this.root?.removeEventListener('bucket:state-updated', this.onStateUpdated);
		document.removeEventListener('shopify:section:load', this.onSectionLoad);
	}

	onSectionLoad = event => {
		if (
			!this.closest(`#shopify-section-${event.detail.sectionId}`) &&
			!this.closest(`#${event.detail.sectionId}`)
		)
			return;

		this.productCards = [...this.querySelectorAll(this.selectors.productCard)];
		this.hydrateFromState();
		this.updateUI();
	};

	onStateUpdated = event => {
		if (event.target !== this.root) return;

		this.hydrateFromState(false);
		this.updateUI(false);
	};

	getQuantitiesMap() {
		const state = this.root?.getState();

		if (!state) return new Map();

		return new Map(
			state.step2Products.map(product => [String(product.id), product.quantity || 0])
		);
	}

	hydrateFromState() {
		const quantities = this.getQuantitiesMap();

		this.productCards.forEach(card => {
			const productId = card.dataset.productId;
			card.dataset.quantity = quantities.get(productId) || 0;
		});
	}

	onClick = event => {
		const increaseButton = event.target.closest(this.selectors.qtyIncrease);
		const decreaseButton = event.target.closest(this.selectors.qtyDecrease);
		const continueButton = event.target.closest(this.selectors.stepContinue);
		const backButton = event.target.closest(this.selectors.stepBack);
		const closeButton = event.target.closest(this.selectors.fullClose);

		if (increaseButton) {
			this.onIncrease(increaseButton.closest(this.selectors.productCard));
			return;
		}

		if (decreaseButton) {
			this.onDecrease(decreaseButton.closest(this.selectors.productCard));
			return;
		}

		if (continueButton && !continueButton.disabled) {
			this.dispatchEvent(
				new CustomEvent('bucket:next-step', { bubbles: true })
			);
			return;
		}

		if (backButton) {
			this.dispatchEvent(
				new CustomEvent('bucket:prev-step', { bubbles: true })
			);
			return;
		}

		if (closeButton) {
			this.hideFullPopup();
		}
	};

	isProductLimitEnabled(card) {
		return card?.hasAttribute('data-max-quantity-limit-enabled');
	}

	getProductMax(card) {
		return Number(card.dataset.maxQuantity) || 4;
	}

	onIncrease(card) {
		if (!card) return;

		const currentQty = Number(card.dataset.quantity) || 0;
		const totalQty = this.getTotalQuantity();

		if (totalQty >= this.maxItems) {
			this.showFullPopup();
			return;
		}

		if (
			this.isProductLimitEnabled(card) &&
			currentQty >= this.getProductMax(card)
		) {
			this.showLimitMessage(card);
			return;
		}

		card.dataset.quantity = currentQty + 1;
		this.persistState();
		this.updateUI();
	}

	onDecrease(card) {
		if (!card) return;

		const currentQty = Number(card.dataset.quantity) || 0;

		if (currentQty <= 0) return;

		card.dataset.quantity = currentQty - 1;
		this.hideLimitMessage(card);
		this.persistState();
		this.updateUI();
	}

	getTotalQuantity() {
		return this.productCards.reduce(
			(total, card) => total + (Number(card.dataset.quantity) || 0),
			0
		);
	}

	persistState() {
		const state = this.root?.getState();

		if (!state) return;

		const step2Products = state.step2Products.map(product => {
			const card = this.productCards.find(
				item => String(item.dataset.productId) === String(product.id)
			);

			return {
				...product,
				quantity: card ? Number(card.dataset.quantity) || 0 : 0
			};
		});

		this.root?.dispatchEvent(
			new CustomEvent('bucket:update-state', {
				bubbles: true,
				detail: { step2Products }
			})
		);
	}

	updateUI(shouldPersist = false) {
		const totalQty = this.getTotalQuantity();
		const canContinue = totalQty >= this.minItems;

		this.productCards.forEach(card => {
			const qty = Number(card.dataset.quantity) || 0;
			const qtyValue = card.querySelector(this.selectors.qtyValue);

			if (qtyValue) qtyValue.textContent = `${qty}`;

			card.classList.toggle(this.stateClasses.selected, qty > 0);

			if (
				this.isProductLimitEnabled(card) &&
				qty >= this.getProductMax(card)
			) {
				this.showLimitMessage(card);
			} else {
				this.hideLimitMessage(card);
			}
		});

		if (this.statusCountElement) {
			this.statusCountElement.textContent = this.replacePlaceholders(
				this.statusCountText,
				{ count: totalQty, min: this.minItems }
			);
		}

		if (this.statusMessageElement) {
			this.statusMessageElement.textContent = this.replacePlaceholders(
				this.statusMessageText,
				{ count: totalQty, min: this.minItems }
			);
			this.statusMessageElement.hidden = canContinue;
		}

		if (this.continueButton) {
			this.continueButton.disabled = !canContinue;
			this.continueButton.classList.toggle(
				this.stateClasses.disabled,
				!canContinue
			);
		}

		if (shouldPersist) this.persistState();
	}

	showLimitMessage(card) {
		const limitMessageElement = card.querySelector(
			this.selectors.limitMessage
		);

		if (!limitMessageElement) return;

		const productTitle =
			card.dataset.productTitle ||
			card.querySelector('.bucket-product-card__title')?.textContent?.trim() ||
			'';
		const template =
			card.dataset.limitMessage || 'LIMIT [limit] [product] PER BUCKET';

		limitMessageElement.textContent = this.replacePlaceholders(template, {
			limit: this.getProductMax(card),
			product: productTitle.toUpperCase()
		});
		limitMessageElement.hidden = false;
	}

	hideLimitMessage(card) {
		const limitMessageElement = card.querySelector(
			this.selectors.limitMessage
		);

		if (!limitMessageElement) return;

		limitMessageElement.hidden = true;
		limitMessageElement.textContent = '';
	}

	showFullPopup() {
		if (!this.fullPopup) return;

		this.fullPopup.hidden = false;
	}

	hideFullPopup() {
		if (!this.fullPopup) return;

		this.fullPopup.hidden = true;
	}

	replacePlaceholders(template, values = {}) {
		return template
			.replace(/\[count\]/g, values.count ?? '')
			.replace(/\[min\]/g, values.min ?? '')
			.replace(/\[limit\]/g, values.limit ?? '')
			.replace(/\[product\]/g, values.product ?? '');
	}
}

class StepThreeComponent extends HTMLElement {
	selectors = {
		seasoningCard: '[data-seasoning-card]',
		freeLabel: '[data-seasoning-free-label]',
		seasoningPrice: '[data-seasoning-price]',
		selectorEmpty: '.bucket-seasoning-card__selector-empty',
		selectorCheck: '.bucket-seasoning-card__selector-check',
		stepContinue: '[data-step-continue]',
		stepBack: '[data-step-back]'
	};

	stateClasses = {
		selected: 'is-selected',
		disabled: 'is-disabled'
	};

	connectedCallback() {
		this.root = this.closest('build-your-bucket');
		this.freeLabelText = this.dataset.freeLabel || 'FREE';
		this.seasoningCards = [...this.querySelectorAll(this.selectors.seasoningCard)];
		this.continueButton = this.querySelector(this.selectors.stepContinue);

		this.addEventListener('click', this.onClick);
		this.root?.addEventListener('bucket:state-updated', this.onStateUpdated);

		document.addEventListener('shopify:section:load', this.onSectionLoad);

		this.hydrateFromState();
		this.updateUI();
	}

	disconnectedCallback() {
		this.removeEventListener('click', this.onClick);
		this.root?.removeEventListener('bucket:state-updated', this.onStateUpdated);
		document.removeEventListener('shopify:section:load', this.onSectionLoad);
	}

	onSectionLoad = event => {
		if (
			!this.closest(`#shopify-section-${event.detail.sectionId}`) &&
			!this.closest(`#${event.detail.sectionId}`)
		)
			return;

		this.seasoningCards = [...this.querySelectorAll(this.selectors.seasoningCard)];
		this.hydrateFromState();
		this.updateUI();
	};

	onStateUpdated = event => {
		if (event.target !== this.root) return;

		this.hydrateFromState();
		this.updateUI(false);
	};

	hydrateFromState() {
		const state = this.root?.getState();

		if (!state) return;

		this.seasoningCards.forEach(card => {
			const seasoning = state.step3Seasonings.find(
				item => String(item.id) === String(card.dataset.productId)
			);

			card.dataset.selected = seasoning?.selected ? 'true' : 'false';
		});
	}

	onClick = event => {
		const seasoningCard = event.target.closest(this.selectors.seasoningCard);
		const continueButton = event.target.closest(this.selectors.stepContinue);
		const backButton = event.target.closest(this.selectors.stepBack);

		if (seasoningCard && this.contains(seasoningCard)) {
			this.toggleSelection(seasoningCard);
			return;
		}

		if (continueButton && !continueButton.disabled) {
			this.dispatchEvent(
				new CustomEvent('bucket:next-step', { bubbles: true })
			);
			return;
		}

		if (backButton) {
			this.dispatchEvent(
				new CustomEvent('bucket:prev-step', { bubbles: true })
			);
		}
	};

	toggleSelection(card) {
		const isSelected = card.dataset.selected === 'true';
		const willSelect = !isSelected;
		const isNonProduct = card.dataset.isNonProduct === 'true';

		if (willSelect) {
			this.seasoningCards.forEach(otherCard => {
				if (otherCard === card) return;

				if (isNonProduct || otherCard.dataset.isNonProduct === 'true') {
					otherCard.dataset.selected = 'false';
				}
			});
		}

		card.dataset.selected = willSelect ? 'true' : 'false';
		this.persistState();
		this.updateUI();
	}

	persistState() {
		const state = this.root?.getState();

		if (!state) return;

		let step3Seasonings = state.step3Seasonings.map(seasoning => ({ ...seasoning }));
		let maxOrder = Math.max(
			0,
			...step3Seasonings
				.filter(seasoning => seasoning.selected && seasoning.selectionOrder)
				.map(seasoning => seasoning.selectionOrder)
		);

		this.seasoningCards.forEach(card => {
			const seasoningIndex = step3Seasonings.findIndex(
				item => String(item.id) === String(card.dataset.productId)
			);

			if (seasoningIndex === -1) return;

			const seasoning = step3Seasonings[seasoningIndex];
			const isSelected = card.dataset.selected === 'true';
			const wasSelected = seasoning.selected;

			if (isSelected && !wasSelected) {
				maxOrder += 1;
				step3Seasonings[seasoningIndex] = {
					...seasoning,
					selected: true,
					selectionOrder: maxOrder
				};
				return;
			}

			if (!isSelected && wasSelected) {
				const removedOrder = seasoning.selectionOrder;
				step3Seasonings[seasoningIndex] = {
					...seasoning,
					selected: false,
					selectionOrder: null
				};

				step3Seasonings = step3Seasonings.map(item => {
					if (!item.selected || !item.selectionOrder || !removedOrder) {
						return item;
					}

					if (item.selectionOrder > removedOrder) {
						return {
							...item,
							selectionOrder: item.selectionOrder - 1
						};
					}

					return item;
				});
			}
		});

		if (!step3Seasonings.some(seasoning => seasoning.selected)) {
			step3Seasonings = BucketStorage.applyDefaultSeasoningSelection(step3Seasonings);
		}

		this.root?.dispatchEvent(
			new CustomEvent('bucket:update-state', {
				bubbles: true,
				detail: { step3Seasonings }
			})
		);
	}

	updateUI(shouldPersist = false) {
		const state = this.root?.getState();
		const hasSelection = this.seasoningCards.some(
			card => card.dataset.selected === 'true'
		);

		this.seasoningCards.forEach(card => {
			const seasoning = state?.step3Seasonings.find(
				item => String(item.id) === String(card.dataset.productId)
			);
			const isSelected = card.dataset.selected === 'true';
			const isNonProduct = card.dataset.isNonProduct === 'true';
			const isFirstSelected =
				isSelected &&
				!isNonProduct &&
				(seasoning?.isFree || seasoning?.selectionOrder === 1);
			const freeLabel = card.querySelector(this.selectors.freeLabel);
			const priceEl = card.querySelector(this.selectors.seasoningPrice);
			const selectorEmpty = card.querySelector(this.selectors.selectorEmpty);
			const selectorCheck = card.querySelector(this.selectors.selectorCheck);

			card.classList.toggle(this.stateClasses.selected, isSelected);
			card.setAttribute('aria-pressed', isSelected ? 'true' : 'false');

			if (freeLabel) {
				freeLabel.hidden = !isFirstSelected;
				if (isFirstSelected) freeLabel.textContent = this.freeLabelText;
			}

			if (priceEl) {
				priceEl.style.textDecoration = isFirstSelected ? 'line-through' : '';
			}

			if (selectorEmpty) selectorEmpty.hidden = isSelected;
			if (selectorCheck) selectorCheck.hidden = !isSelected;
		});

		if (this.continueButton) {
			this.continueButton.disabled = !hasSelection;
			this.continueButton.classList.toggle(
				this.stateClasses.disabled,
				!hasSelection
			);
		}

		if (shouldPersist) this.persistState();
	}
}

class StepFourComponent extends HTMLElement {
	selectors = {
		giftToggle: '[data-gift-toggle]',
		giftMessageWrapper: '[data-gift-message-wrapper]',
		giftMessage: '[data-gift-message]',
		characterCount: 'tr-character-count',
		stepContinue: '[data-step-continue]',
		stepBack: '[data-step-back]'
	};

	connectedCallback() {
		this.root = this.closest('build-your-bucket');
		this.giftToggle = this.querySelector(this.selectors.giftToggle);
		this.giftMessageWrapper = this.querySelector(
			this.selectors.giftMessageWrapper
		);
		this.giftMessage = this.querySelector(this.selectors.giftMessage);
		this.characterCount = this.querySelector(this.selectors.characterCount);

		this.addEventListener('click', this.onClick);
		this.giftToggle?.addEventListener('change', this.onGiftToggleChange);
		this.giftMessage?.addEventListener('input', this.onGiftMessageInput);
		this.root?.addEventListener('bucket:state-updated', this.onStateUpdated);

		document.addEventListener('shopify:section:load', this.onSectionLoad);

		this.hydrateFromState();
		this.updateUI();
	}

	disconnectedCallback() {
		this.removeEventListener('click', this.onClick);
		this.giftToggle?.removeEventListener('change', this.onGiftToggleChange);
		this.giftMessage?.removeEventListener('input', this.onGiftMessageInput);
		this.root?.removeEventListener('bucket:state-updated', this.onStateUpdated);
		document.removeEventListener('shopify:section:load', this.onSectionLoad);
	}

	onSectionLoad = event => {
		if (
			!this.closest(`#shopify-section-${event.detail.sectionId}`) &&
			!this.closest(`#${event.detail.sectionId}`)
		)
			return;

		this.hydrateFromState();
		this.updateUI();
	};

	onStateUpdated = event => {
		if (event.target !== this.root) return;

		this.hydrateFromState();
		this.updateUI();
	};

	hydrateFromState() {
		const gift = this.root?.getState()?.step4Gift;

		if (!gift || !this.giftToggle) return;

		this.giftToggle.checked = gift.enabled;
		if (this.giftMessage) this.giftMessage.value = gift.message || '';
		this.characterCount?.updateCount?.();
	}

	onGiftToggleChange = () => {
		this.persistState();
		this.updateUI();
	};

	onGiftMessageInput = () => {
		this.persistState();
	};

	onClick = event => {
		const continueButton = event.target.closest(this.selectors.stepContinue);
		const backButton = event.target.closest(this.selectors.stepBack);

		if (continueButton) {
			this.persistState();
			this.dispatchEvent(
				new CustomEvent('bucket:next-step', { bubbles: true })
			);
			return;
		} 

		if (backButton) {
			this.dispatchEvent(
				new CustomEvent('bucket:prev-step', { bubbles: true })
			);
		}
	};

	persistState() {
		this.root?.dispatchEvent(
			new CustomEvent('bucket:update-state', {
				bubbles: true,
				detail: {
					step4Gift: {
						enabled: this.giftToggle?.checked || false,
						message: this.giftMessage?.value || ''
					}
				}
			})
		);
	}

	updateUI() {
		const isEnabled = this.giftToggle?.checked || false;

		if (this.giftMessageWrapper) {
			this.giftMessageWrapper.hidden = !isEnabled;
		}

		if (isEnabled) {
			this.characterCount?.updateCount?.();
		}
	}
}

class StepFiveComponent extends HTMLElement {
	selectors = {
		reviewContents: '[data-review-contents]',
		reviewSeasonings: '[data-review-seasonings]',
		reviewIncluded: '[data-review-included]',
		reviewGift: '[data-review-gift]',
		reviewEdit: '[data-review-edit]',
		stepBack: '[data-step-back]',
		addToCart: '[data-add-to-cart]',
		addToCartError: '[data-add-to-cart-error]'
	};

	stateClasses = {
		disabled: 'is-disabled',
		loading: 'is-loading'
	};

	isAddingToCart = false;

	connectedCallback() {
		this.root = this.closest('build-your-bucket');
		this.giftYesLabel = this.dataset.giftYesLabel || 'Yes, make it a gift';
		this.giftNoLabel = this.dataset.giftNoLabel || 'No, thanks';
		this.addErrorText =
			this.dataset.addErrorText || 'Unable to add this bucket to the cart.';

		this.reviewContents = this.querySelector(this.selectors.reviewContents);
		this.reviewSeasonings = this.querySelector(this.selectors.reviewSeasonings);
		this.reviewIncluded = this.querySelector(this.selectors.reviewIncluded);
		this.reviewGift = this.querySelector(this.selectors.reviewGift);
		this.addToCartButton = this.querySelector(this.selectors.addToCart);
		this.addToCartError = this.querySelector(this.selectors.addToCartError);
		this.cart =
			document.querySelector('cart-notification') ||
			document.querySelector('cart-drawer');

		this.addEventListener('click', this.onClick);
		this.root?.addEventListener('bucket:state-updated', this.onStateUpdated);

		document.addEventListener('shopify:section:load', this.onSectionLoad);

		this.renderReview();
	}

	disconnectedCallback() {
		this.removeEventListener('click', this.onClick);
		this.root?.removeEventListener('bucket:state-updated', this.onStateUpdated);
		document.removeEventListener('shopify:section:load', this.onSectionLoad);
	}

	onSectionLoad = event => {
		if (
			!this.closest(`#shopify-section-${event.detail.sectionId}`) &&
			!this.closest(`#${event.detail.sectionId}`)
		)
			return;

		this.renderReview();
	};

	onStateUpdated = event => {
		if (event.target !== this.root) return;

		this.renderReview();
	};

	onClick = event => {
		const editButton = event.target.closest(this.selectors.reviewEdit);
		const backButton = event.target.closest(this.selectors.stepBack);
		const addToCartButton = event.target.closest(this.selectors.addToCart);

		if (editButton) {
			this.dispatchEvent(
				new CustomEvent('bucket:go-to-step', {
					bubbles: true,
					detail: { step: Number(editButton.dataset.reviewEdit) }
				})
			);
			return;
		}

		if (backButton) {
			this.dispatchEvent(
				new CustomEvent('bucket:prev-step', { bubbles: true })
			);
			return;
		}

		if (addToCartButton) {
			this.addToCart();
		}
	};

	getBundleId() {
		if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
			return crypto.randomUUID();
		}

		return `bucket-${Date.now()}-${Math.random().toString(16).slice(2)}`;
	}

	getMainVariantId(state) {
		return state.mainProduct?.variantId || null;
	}

	getBucketVariantId(state) {
		return state.bucketBase?.variantId || null;
	}

	buildItemProperties({
		role,
		price,
		bundleId,
		parentVariantId,
		parentPrice,
		makeItGift,
		giftMessage,
		included,
		extra = {}
	}) {
		return {
			_bundleId: bundleId,
			_role: role,
			_parentProduct: String(parentVariantId),
			_parentPrice: String(parentPrice),
			_price: String(price),
			_makeItGift: makeItGift ? 'true' : 'false',
			...(makeItGift ? { _giftMessage: giftMessage } : {}),
			...(included ? { _included: included } : {}),
			...extra
		};
	}

	buildCartItems() {
		const state = this.root?.getState();

		if (!state) return [];

		const mainVariantId = this.getMainVariantId(state);

		if (!mainVariantId) return [];

		const parentPrice = state.mainProduct?.price || 0;
		const bucketVariantId = this.getBucketVariantId(state);
		const bundleId = this.getBundleId();
		const makeItGift = Boolean(state.step4Gift?.enabled);
		const giftMessage = state.step4Gift?.message?.trim() || '';
		const included = state.included?.trim() || '';
		const itemContext = {
			bundleId,
			parentVariantId: mainVariantId,
			parentPrice,
			makeItGift,
			giftMessage,
			included
		};
		const fills = (state.step2Products || []).filter(
			product => product.quantity > 0 && product.variantId
		);
		const seasonings = (state.step3Seasonings || [])
			.filter(seasoning => BucketStorage.isProductSeasoning(seasoning))
			.sort(
				(a, b) =>
					(a.selectionOrder ?? Infinity) - (b.selectionOrder ?? Infinity)
			);

		return [
			...(bucketVariantId && bucketVariantId !== mainVariantId
				? [
						{
							id: bucketVariantId,
							quantity: 1,
							properties: this.buildItemProperties({
								...itemContext,
								role: 'bucket',
								price: state.bucketBase?.price || 0
							})
						}
					]
				: []),
			...fills.map(fill => ({
				id: fill.variantId,
				quantity: fill.quantity,
				properties: this.buildItemProperties({
					...itemContext,
					role: 'fill',
					price: fill.price || 0
				})
			})),
			...seasonings.map((seasoning, index) => ({
				id: seasoning.variantId,
				quantity: seasoning.quantity || 1,
				properties: this.buildItemProperties({
					...itemContext,
					role: 'seasoning',
					price: seasoning.effectivePrice ?? seasoning.price ?? 0,
					extra: { _seasoningIndex: String(index) }
				})
			}))
		];
	}

	setAddToCartBusy(isBusy) {
		if (!this.addToCartButton) return;

		this.addToCartButton.disabled = isBusy;
		this.addToCartButton.setAttribute('aria-busy', isBusy ? 'true' : 'false');
		this.addToCartButton.classList.toggle(this.stateClasses.disabled, isBusy);
		this.addToCartButton.classList.toggle(this.stateClasses.loading, isBusy);
	}

	showAddToCartError(message) {
		if (!this.addToCartError) return;

		this.addToCartError.textContent = message || this.addErrorText;
		this.addToCartError.hidden = false;
	}

	clearAddToCartError() {
		if (!this.addToCartError) return;

		this.addToCartError.hidden = true;
		this.addToCartError.textContent = '';
	}

	syncGiftState() {
		this.root?.querySelector('step-four-component')?.persistState?.();
	}

	async addToCart() {
		if (this.isAddingToCart) return;

		this.syncGiftState();
		const items = this.buildCartItems();

		if (!items.length) {
			this.showAddToCartError(this.addErrorText);
			return;
		}

		console.log('[cart.add.js] items:', items);

		this.isAddingToCart = true;
		this.setAddToCartBusy(true);
		this.clearAddToCartError();

		const config =
			typeof fetchConfig === 'function'
				? fetchConfig('javascript')
				: {
						method: 'POST',
						headers: {
							'Content-Type': 'application/json',
							Accept: 'application/javascript'
						}
					};
		const body = { items };

		if (this.cart) {
			body.sections = this.cart
				.getSectionsToRender()
				.map(section => section.id)
				.join(',');
			body.sections_url = window.location.pathname;
			this.cart.setActiveElement?.(document.activeElement);
		}

		try {
			console.log('[cart.add.js] payload:', body);
			const cartAddUrl = window.routes?.cart_add_url || '/cart/add.js';
			const response = await fetch(
				cartAddUrl.endsWith('.js') ? cartAddUrl : `${cartAddUrl}.js`,
				{
					...config,
					body: JSON.stringify(body)
				}
			);
			const data = await response.json();

			if (data.status) {
				this.showAddToCartError(
					data.description || data.message || this.addErrorText
				);
				return;
			}

			if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
				publish(PUB_SUB_EVENTS.cartUpdate, {
					source: 'cart-items',
					cartData: data
				});
			}

			if (this.cart && data.sections) {
				if (!data.key && data.items?.[0]?.key) {
				  data.key = data.items[0].key;
				}
			  
				// Cart was empty before this add, so <cart-drawer> may still carry a
				// stale "is-empty" class. cart-drawer.js uses that class to decide
				// which element to trap focus in, and picks .drawer__inner-empty —
				// which no longer exists once items are rendered, causing a null
				// reference crash that aborts the drawer render. Clear it up front.
				this.cart.classList.remove('is-empty');
				this.cart.querySelector('.drawer__inner')?.classList.remove('is-empty');
			  
				this.cart.renderContents(data);
				return;
			  }

			window.location = window.routes?.cart_url || '/cart';
		} catch (error) {
			this.showAddToCartError(error.message || this.addErrorText);
		} finally {
			this.isAddingToCart = false;
			this.setAddToCartBusy(false);
		}
	}

	renderReview() {
		const state = this.root?.getState();

		if (!state) return;

		if (this.reviewContents) {
			const selectedProducts = state.step2Products.filter(
				product => product.quantity > 0
			);

			this.reviewContents.innerHTML = selectedProducts.length
				? selectedProducts
						.map(
							product =>
								`<span class="bucket-review-tag">${product.title} &times;${product.quantity}</span>`
						)
						.join('')
				: '<span class="bucket-review-tag bucket-review-tag--empty">No items selected</span>';
		}

		if (this.reviewSeasonings) {
			const selectedSeasonings = state.step3Seasonings
				.filter(seasoning => seasoning.selected)
				.sort(
					(a, b) =>
						(a.selectionOrder ?? Infinity) - (b.selectionOrder ?? Infinity)
				);

			this.reviewSeasonings.innerHTML = selectedSeasonings.length
				? selectedSeasonings
						.map(seasoning => {
							const freeSuffix =
								!seasoning.isNonProduct && seasoning.isFree ? ' (FREE)' : '';
							return `<span class="bucket-review-tag">${seasoning.title}${freeSuffix}</span>`;
						})
						.join('')
				: '<span class="bucket-review-tag bucket-review-tag--empty">No seasoning selected</span>';
		}

		if (this.reviewIncluded) {
			this.reviewIncluded.textContent = state.included || '';
		}

		if (this.reviewGift) {
			if (state.step4Gift.enabled) {
				this.reviewGift.textContent = state.step4Gift.message
					? `${this.giftYesLabel} — ${state.step4Gift.message}`
					: this.giftYesLabel;
			} else {
				this.reviewGift.textContent = this.giftNoLabel;
			}
		} 
	}
}

class BucketSidebarComponent extends HTMLElement {
	selectors = {
		bucketVisual: '[data-bucket-visual]',
		slotsStatus: '[data-slots-status]',
		priceBase: '[data-price-base]',
		priceItemsRow: '[data-price-items-row]',
		priceItemsLabel: '[data-price-items-label]',
		priceItems: '[data-price-items]',
		priceSeasoningRow: '[data-price-seasoning-row]',
		priceSeasoning: '[data-price-seasoning]',
		priceTotal: '[data-price-total]',
		giftRow: '[data-gift-row]',
		giftValue: '[data-gift-value]'
	};

	connectedCallback() {
		this.root = this.closest('build-your-bucket');
		this.slotsFilledText =
			this.dataset.slotsFilledText || '[count] of [max] slots filled';
		this.slotsFilledContinueText =
			this.dataset.slotsFilledContinueText ||
			'[count] of [max] slots filled — [remaining] more to continue';
		this.requiredLabel = this.dataset.requiredLabel || 'required';
		this.itemsLabelText =
			this.dataset.itemsLabel || 'ITEMS ([count] OF [max])';
		this.seasoningValueText =
			this.dataset.seasoningValue || '[count]X [price]';
		this.giftYesLabel = this.dataset.giftYesLabel || 'YES';
		this.giftNoLabel = this.dataset.giftNoLabel || 'NO';

		this.bucketVisual = this.querySelector(this.selectors.bucketVisual);
		this.slotsStatus = this.querySelector(this.selectors.slotsStatus);
		this.priceBase = this.querySelector(this.selectors.priceBase);
		this.priceItemsRow = this.querySelector(this.selectors.priceItemsRow);
		this.priceItemsLabel = this.querySelector(
			this.selectors.priceItemsLabel
		);
		this.priceItems = this.querySelector(this.selectors.priceItems);
		this.priceSeasoningRow = this.querySelector(
			this.selectors.priceSeasoningRow
		);
		this.priceSeasoning = this.querySelector(this.selectors.priceSeasoning);
		this.priceTotal = this.querySelector(this.selectors.priceTotal);
		this.giftRow = this.querySelector(this.selectors.giftRow);
		this.giftValue = this.querySelector(this.selectors.giftValue);

		this.root?.addEventListener('bucket:state-updated', this.onStateUpdated);
		document.addEventListener('shopify:section:load', this.onSectionLoad);

		this.render();
	}

	disconnectedCallback() {
		this.root?.removeEventListener('bucket:state-updated', this.onStateUpdated);
		document.removeEventListener('shopify:section:load', this.onSectionLoad);
	}

	onSectionLoad = event => {
		if (
			!this.closest(`#shopify-section-${event.detail.sectionId}`) &&
			!this.closest(`#${event.detail.sectionId}`)
		)
			return;

		this.render();
	};

	onStateUpdated = event => {
		if (event.target !== this.root) return;

		this.render();
	};

	getFilledSlotTitles(state) {
		const titles = [];

		(state.step2Products || []).forEach(product => {
			const quantity = product.quantity || 0;

			for (let index = 0; index < quantity; index += 1) {
				titles.push(product.title);
			}
		});

		return titles;
	}

	escapeHtml(text) {
		const element = document.createElement('span');
		element.textContent = text;
		return element.innerHTML;
	}

	formatMoney(cents) {
		const config = this.root?.config || {};
		return BucketStorage.formatMoney(cents, config.moneyFormat);
	}

	renderBucketVisual(filledTitles, maxItems, minItems) {
		if (!this.bucketVisual) return;

		const filledCount = filledTitles.length;
		const slotsMarkup = Array.from({ length: maxItems }, (_, index) => {
			const title = filledTitles[index] || '';
			const isFilled = Boolean(title);
			const isRequired = !isFilled && index < minItems;

			let slotClass = 'bucket-sidebar__slot';

			if (isFilled) {
				slotClass += ' is-filled';
			} else if (isRequired) {
				slotClass += ' is-required';
			}

			const label = isFilled
				? this.escapeHtml(title)
				: isRequired
					? this.escapeHtml(this.requiredLabel)
					: '';

			return `<div class="${slotClass}"><span>&nbsp;${label}</span></div>`;
		}).join('');

		this.bucketVisual.innerHTML = `<div class="bucket-sidebar__slots">${slotsMarkup}</div>`;
		this.bucketVisual.dataset.filledCount = String(filledCount);
	}

	render() {
		const state = this.root?.getState();
		const config = this.root?.config || {};

		if (!state) return;

		const maxItems = Number(config.maxItems) || 6;
		const minItems = Number(config.minItems) || 3;
		const currentStepIndex = this.root?.getCurrentStepIndex?.() ?? 0;
		const filledTitles = this.getFilledSlotTitles(state);
		const filledCount = filledTitles.length;
		const basePrice = state.bucketBase?.price || state.mainProduct?.price || 0;
		const itemsPrice = (state.step2Products || []).reduce(
			(total, product) =>
				total + (product.price || 0) * (product.quantity || 0),
			0
		);
		const selectedSeasonings = (state.step3Seasonings || [])
			.filter(seasoning => BucketStorage.isProductSeasoning(seasoning))
			.sort(
				(a, b) =>
					(a.selectionOrder ?? Infinity) - (b.selectionOrder ?? Infinity)
			);
		const seasoningPrice = selectedSeasonings.reduce(
			(total, seasoning) =>
				total + (seasoning.effectivePrice ?? seasoning.price ?? 0),
			0
		);
		const showSeasoningPricing =
			currentStepIndex >= 2 && selectedSeasonings.length > 0;
		const showGift = currentStepIndex >= 3;
		const total =
			basePrice + itemsPrice + (showSeasoningPricing ? seasoningPrice : 0);

		this.renderBucketVisual(filledTitles, maxItems, minItems);

		if (this.slotsStatus) {
			const remaining = Math.max(minItems - filledCount, 0);
			const statusTemplate =
				filledCount < minItems
					? this.slotsFilledContinueText
					: this.slotsFilledText;

			this.slotsStatus.textContent = BucketStorage.replacePlaceholders(
				statusTemplate,
				{ count: filledCount, max: maxItems, remaining }
			);
		}

		if (this.priceBase) {
			this.priceBase.textContent = this.formatMoney(basePrice);
		}

		if (this.priceItemsLabel) {
			this.priceItemsLabel.textContent = BucketStorage.replacePlaceholders(
				this.itemsLabelText,
				{ count: filledCount, max: maxItems }
			);
		}

		if (this.priceItems) {
			this.priceItems.textContent = this.formatMoney(itemsPrice);
		}

		if (this.priceSeasoningRow) {
			this.priceSeasoningRow.hidden = !showSeasoningPricing;
		}

		if (this.priceSeasoning && showSeasoningPricing) {
			const seasoningCount = selectedSeasonings.length;

			this.priceSeasoning.textContent = BucketStorage.replacePlaceholders(
				this.seasoningValueText,
				{
					count: seasoningCount,
					price: this.formatMoney(seasoningPrice)
				}
			);
		}

		if (this.priceTotal) {
			this.priceTotal.textContent = this.formatMoney(total);
		}

		if (this.giftRow) {
			this.giftRow.hidden = !showGift;
		}

		if (this.giftValue && showGift) {
			this.giftValue.textContent = state.step4Gift?.enabled
				? this.giftYesLabel
				: this.giftNoLabel;
		}
	}
}

if (!customElements.get('build-your-bucket')) {
	customElements.define('build-your-bucket', BuildYourBucket);
}

if (!customElements.get('step-two-component')) {
	customElements.define('step-two-component', StepTwoComponent);
}

if (!customElements.get('step-three-component')) {
	customElements.define('step-three-component', StepThreeComponent);
}

if (!customElements.get('step-four-component')) {
	customElements.define('step-four-component', StepFourComponent);
}

if (!customElements.get('step-five-component')) {
	customElements.define('step-five-component', StepFiveComponent);
}

if (!customElements.get('bucket-sidebar')) {
	customElements.define('bucket-sidebar', BucketSidebarComponent);
}
